import {
  ECategoryErrorType,
  ECategoryType,
  ECommonDialogType,
  EFeatureFamilyType,
  EDropCategory,
} from '../data/Constants';
import { openDialog } from '../store/states/DialogSlice';

import type {
  ICategory,
  IContent,
  IFeatureFamily,
  IMasterView,
  IOption,
  ISection,
} from '../../types/ITypes';

/**
 * Updates the master view with flags for expanding and collapsing sections having subsections
 * @param {IMasterView} masterView - The master view containing all sections
 * @param {ISection} selectedSection - The section that was selected/clicked
 * @returns {IMasterView | null} Updated master view with section flags or null if selectedSection id is undefined
 */
export const getMasterViewWithFlag = (
  masterView: IMasterView,
  selectedSection: ISection
): IMasterView | null => {
  if (selectedSection?.id === undefined) {
    return null;
  }

  const updateSections = (sections: ISection[], level: number = 0): ISection[] => {
    //looping the sections and update its properties
    //(ex: hasIcon- to show + icon , open: to collapse or expand the section )
    return sections.map((section) => {
      // Increment level only for child sections
      const nextLevel = level + 1;

      // Update properties for parent sections
      const isMultiNumericCategory = checkIsMultiNumericCategory(section.id);
      const type = isMultiNumericCategory
        ? ECategoryType.MultiNumeric.toString()
        : ECategoryType.General.toString();
      let updatedSection = {
        ...section,
        type: type,
        hideAddIcon: isMultiNumericCategory, // hide add icon for multi numeric categories
        hideGeneral: level >= 1, // show general category for only until first level
        open: section.id === selectedSection.id ? !section.open : section.open,
      };

      // Update properties for child sections if any
      if (section.sections.length > 0) {
        updatedSection = {
          ...updatedSection,
          sections: updateSections(section.sections, nextLevel),
        };
      }

      return updatedSection;
    });
  };

  //call the updateSections method to update its properties
  const updatedSections = updateSections(masterView.sections);

  return { ...masterView, sections: updatedSections };
};

/**
 * Checks if a category is a multi-numeric category based on its ID format
 * @param {string} categoryId - The category ID to check
 * @returns {boolean} True if the category ID has more than 2 parts separated by underscores
 */
export function checkIsMultiNumericCategory(categoryId: string): boolean {
  return categoryId?.split('_').length > 2;
}

/**
 * Finds and returns a category from a list based on the category code
 * @param {Array<ICategory>} list - Array of categories to search through
 * @param {string} code - The category code to find
 * @returns {ICategory} The category with the matching code
 */
export const findCategoryWithCode = (list: Array<ICategory>, code: string): ICategory => {
  const item: ICategory = list.find((categoryItem: ICategory) => categoryItem.code === code)!;
  return item;
};

/**
 * Adds a category at root level or as a sub-category
 * Validates if the category already exists and returns an error message if it does
 * @param {IMasterView} masterView - The master view containing all sections
 * @param {ISection} selectedSection - The parent section to add the new category to (null for root level)
 * @param {{ id: string; name: string; type?: string }} newCategoryDetail - Details of the new category to add
 * @returns {string | Array<ISection>} Error message if category exists, otherwise the updated sections array
 */
export const addCategoryAndSubCategory = (
  masterView: IMasterView,
  selectedSection: ISection,
  newCategoryDetail: { id: string; name: string; type?: string }
) => {
  let isCategoryExist: boolean = false;
  let categoryWithNewCategory: Array<ISection> = [];
  const getName = () => {
    const name = newCategoryDetail?.name.toString().split(':');
    if (name.length > 1) {
      return name[1].trim();
    } else {
      return newCategoryDetail?.name;
    }
  };

  const newCategory: ISection = {
    id: newCategoryDetail?.id,
    name: getName(),
    contents: [],
    sections: [],
    hideAddIcon: false,
    type: newCategoryDetail.type,
    hideGeneral: false,
    action: '',
  };

  const checkIfExist = (sections: Array<ISection>) => {
    return sections.filter((section: ISection) => {
      if (section.name === newCategory.name || section.id === newCategory.id) {
        isCategoryExist = true;
      }
      if (!isCategoryExist) {
        checkIfExist(section.sections);
      }
      return section;
    });
  };

  if (masterView?.sections !== undefined && masterView?.sections.length > 0) {
    checkIfExist(masterView.sections);
  }

  if (isCategoryExist) {
    return 'messages.category.categoryAlreadyExist';
  }

  if (selectedSection == null || selectedSection === undefined) {
    categoryWithNewCategory = [...masterView.sections, newCategory];
  } else {
    const addSectionInSubSection = (sections: ISection[]) => {
      const updateSections = sections.map((subSection: ISection) => {
        if (subSection.id === selectedSection.id) {
          const updatedSection = [
            ...subSection.sections,
            {
              ...newCategory,
              hideAddIcon: newCategory.type === ECategoryType.MultiNumeric,
              hideGeneral: true,
            },
          ];
          subSection = { ...subSection, sections: updatedSection };
        }

        if (subSection.sections !== undefined) {
          const newSection: ISection[] = addSectionInSubSection(subSection.sections);
          const data = { ...subSection, sections: newSection };
          return data;
        }

        return subSection;
      });
      return updateSections;
    };

    categoryWithNewCategory = addSectionInSubSection(masterView.sections);
  }
  return categoryWithNewCategory;
};

/**
 * Updates the multi-numeric category information in the master view data
 * @param {IMasterView | ISection} masterview - The master view or section to update
 * @param {string} previousCategoryId - Previous category id
 * @param {IOption} newCategory - New category object
 * @returns {boolean} True if the category was successfully updated
 */
export function updateMultiNumericCategory(
  masterview: IMasterView | ISection,
  previousCategoryId: string,
  newCategory: IOption
): boolean {
  return masterview.sections.some((section) => {
    if (section.id === previousCategoryId) {
      section.id = newCategory.code;
      section.name = newCategory.name;
      return true;
    }
    if (section.sections) {
      return updateMultiNumericCategory(section, previousCategoryId, newCategory);
    }
    return false;
  });
}

/**
 * Gets the description for a feature or feature family
 * @param {IContent | IFeatureFamily} item - The content item or feature family
 * @param {IFeatureFamily[]} featureFamilyList - List of all feature families
 * @returns {string} The description of the feature family or the code if not found
 */
export const getFeatureDescription = (
  item: IContent | IFeatureFamily,
  featureFamilyList: IFeatureFamily[]
): string => {
  // If it's already a FeatureFamily type, return its description
  if ('description' in item) {
    return item.description;
  }
  // If it's a Content type, find the matching feature family
  const featureFamily = featureFamilyList.find((ff) => ff.code === item.code);
  return featureFamily?.description ?? item.code;
};

/**
 * Determines the icon type from content icon object
 * @param {{ familyType: string; type: string }} contentIcon - The content icon object containing familyType and/or type
 * @returns {string | undefined} The familyType if available, otherwise the type, or undefined if neither exists
 */
export const getIconType = (contentIcon: { familyType: string; type: string }) => {
  switch (true) {
    case contentIcon?.familyType !== undefined:
      return contentIcon.familyType;
    case contentIcon?.type !== undefined:
      return contentIcon.type;
    default:
      return contentIcon.type;
  }
};

/**
 * Creates updated category data with product code and normalized sections
 * @param {string} productId - The product code
 * @param {ISection[]} sections - Array of sections to normalize
 * @returns {{ productCode: string; sections: ISection[] }} Formatted category data object
 */
export const getUpdatedCategoryData = (productId: string, sections: ISection[]) => {
  return {
    productCode: productId,
    sections: sections.map((section) => ({
      ...section,
      contents: section.contents ?? [],
      sections: section.sections ?? [],
    })),
  };
};

/**
 * Recursively finds a section by its ID in the section tree
 * @param {ISection[]} sections - Array of sections to search through
 * @param {string} id - The ISection ID to find
 * @returns {ISection | null} The found ISection or null if not found
 */
export const findSectionById = (sections: ISection[], id: string): ISection | null => {
  for (const section of sections) {
    if (section.id === id) return section;
    if (section.sections) {
      const found = findSectionById(section.sections, id);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Checks if the target drop zone is a multi-numeric category
 * @param {any} over - The drag and drop 'over' object containing target information
 * @returns {boolean} True if the target is MultiNumeric or Numeric type
 */
export const isTargetMultiNumeric = (over: any): boolean => {
  const overItemType = over.data?.current?.itemType;
  if (overItemType === ECategoryType.MultiNumeric || overItemType === EFeatureFamilyType.Numeric) {
    return true;
  }

  return false;
};

/**
 * Determines which zone/container the item is being dragged to
 * @param {any} over - The drag and drop 'over' object containing target zone information
 * @returns {string} The target zone identifier (Assigned, Unassigned, or category ID)
 */
export const determineTargetZone = (over: any): string => {
  if (over.data?.current?.container === EDropCategory.Assigned) {
    return EDropCategory.Assigned;
  } else if (over.data?.current?.type === EDropCategory.Category) {
    return over.id as string;
  } else if (over.id === EDropCategory.Assigned || over.id === EDropCategory.Unassigned) {
    return over.id as string;
  } else {
    return over.data?.current?.container ?? (over.id as string);
  }
};

/**
 * Creates a deep clone of sections array using structured clone
 * @param {ISection[]} sections - The sections array to clone
 * @returns {ISection[]} A deep copy of the sections array
 */
export const deepCloneSections = (sections: ISection[]): ISection[] =>
  JSON.parse(JSON.stringify(sections));

/**
 * Recursively finds a section by ID in the section tree
 * @param {ISection[]} nodes - Array of section nodes to search through
 * @param {string} id - The section ID to find
 * @returns {ISection | null} The found section or null if not found
 */
export const findSectionInTree = (nodes: ISection[], id: string): ISection | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.sections) {
      const found = findSectionInTree(node.sections, id);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Finds the parent section of a child section by ID
 * @param {ISection[]} sections - Array of sections to search through
 * @param {string} childId - The ID of the child section whose parent to find
 * @param {ISection | null} parent - The current parent section (used internally for recursion)
 * @returns {ISection | null} The parent section or null if not found
 */
export function findParent(
  sections: ISection[],
  childId: string,
  parent: ISection | null = null
): ISection | null {
  for (const section of sections) {
    if (section.id === childId) return parent;
    if (section.sections) {
      const found = findParent(section.sections, childId, section);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Finds and removes a node from the section tree, and locates the target node
 * @param {ISection[]} nodes - Array of section nodes
 * @param {string} draggedId - The ID of the node being dragged
 * @param {string} targetId - The ID of the target node
 * @returns {{ newSections: ISection[]; draggedNode: ISection | null; targetNode: ISection | null }}
 * Object containing updated sections, dragged node, and target node
 */
export function findAndRemoveNode(
  nodes: ISection[],
  draggedId: string,
  targetId: string
): { newSections: ISection[]; draggedNode: ISection | null; targetNode: ISection | null } {
  let draggedNode: ISection | null = null;
  let targetNode: ISection | null = null;

  function helper(nodes: ISection[]): ISection[] {
    return nodes.filter((node) => {
      if (node.id === draggedId) {
        draggedNode = node;
        return false;
      }
      if (node.id === targetId) {
        targetNode = node;
      }
      if (node.sections) {
        node.sections = helper(node.sections);
      }
      return true;
    });
  }

  const newSections = helper(nodes);
  return { newSections, draggedNode, targetNode };
}

/**
 * Checks if the drag operation is attempting to drop a MultiNumeric category under another MultiNumeric category
 * @param {ISection} draggedNode - The section being dragged
 * @param {ISection} targetNode - The target section
 * @returns {boolean} True if both nodes are MultiNumeric type
 */
function isMultiNumericDrop(draggedNode: ISection, targetNode: ISection): boolean {
  return (
    draggedNode.type === ECategoryType.MultiNumeric &&
    targetNode.type === ECategoryType.MultiNumeric
  );
}

/**
 * Checks if a category is being dropped onto itself (same name validation)
 * @param {ISection} draggedNode - The section being dragged
 * @param {ISection} targetNode - The target section
 * @returns {boolean} True if both nodes have the same name
 */
function isSelfDrop(draggedNode: ISection, targetNode: ISection): boolean {
  return draggedNode.name === targetNode.name;
}

/**
 * Checks if any category is being dropped under a MultiNumeric category
 * @param {ISection} targetNode - The target section
 * @returns {boolean} True if the target is a MultiNumeric category
 */
function isDropUnderMultiNumeric(targetNode: ISection): boolean {
  return targetNode.type === ECategoryType.MultiNumeric;
}

/**
 * Checks if a top-level non-numeric category already has a non-numeric subcategory
 * @param {ISection[]} clonedSections - The sections array
 * @param {ISection} draggedNode - The section being dragged
 * @returns {boolean} True if it's a top-level non-numeric category with a non-numeric subcategory
 */
function isTopLevelNonNumericWithSubcategory(
  clonedSections: ISection[],
  draggedNode: ISection
): boolean {
  const isTopLevelNonNumeric =
    draggedNode.type !== ECategoryType.MultiNumeric && !findParent(clonedSections, draggedNode.id);

  const hasNonNumericSubcategory =
    isTopLevelNonNumeric &&
    Array.isArray(draggedNode.sections) &&
    draggedNode.sections.some((sub) => sub.type !== ECategoryType.MultiNumeric);

  return isTopLevelNonNumeric && hasNonNumericSubcategory;
}

/**
 * Validates that non-numeric categories can only be dropped to non-numeric top-level categories
 * @param {ISection[]} clonedSections - The sections array
 * @param {ISection} draggedNode - The section being dragged
 * @param {ISection} targetNode - The target section
 * @returns {boolean} True if attempting to drop a non-numeric category to a subcategory
 */
function isNonNumericDropToSubcategory(
  clonedSections: ISection[],
  draggedNode: ISection,
  targetNode: ISection
): boolean {
  if (
    draggedNode.type !== ECategoryType.MultiNumeric &&
    targetNode.type !== ECategoryType.MultiNumeric
  ) {
    const parentOfTarget = findParent(clonedSections, targetNode.id);
    return !!parentOfTarget;
  }
  return false;
}

/**
 * Validates if a category drag and drop operation is invalid based on multiple rules
 * @param {ISection[]} clonedSections - The cloned sections array
 * @param {ISection} draggedNode - The section being dragged
 * @param {ISection} targetNode - The target section
 * @param {boolean} showError - Whether to display error dialog
 * @param {any} dispatch - Redux dispatch function
 * @param {any} t - Translation function
 * @returns {boolean} True if the drop operation is invalid
 */
export function isInvalidCategoryDrop(
  clonedSections: ISection[],
  draggedNode: ISection,
  targetNode: ISection,
  showError: boolean,
  dispatch: any,
  t: any
): boolean {
  const validationChecks = [
    () => isMultiNumericDrop(draggedNode, targetNode),
    () => isSelfDrop(draggedNode, targetNode),
    () => isDropUnderMultiNumeric(targetNode),
    () => isTopLevelNonNumericWithSubcategory(clonedSections, draggedNode),
    () => isNonNumericDropToSubcategory(clonedSections, draggedNode, targetNode),
  ];

  const isInvalid = validationChecks.some((check) => check());

  if (isInvalid && showError) {
    showCategoryError(dispatch, t, ECategoryErrorType.INVALID_DRAG_DROP);
  }

  return isInvalid;
}

/**
 * Validates if an item can be dropped into a specific category/section
 * For MultiNumeric sections, only Numeric items are allowed
 * For other sections, all item types are allowed
 *
 * @param sectionType - The type of the target section
 * @param itemType - The type of the item being dragged
 * @param isTargetCategory - Optional flag for whether target is a category section
 * @returns Whether the item can be dropped in the target
 */
export const isValidItemDrop = (
  sectionType: string | undefined,
  itemType: string | undefined,
  isTargetCategory: boolean = false
): boolean => {
  // For MultiNumeric sections, only accept Numeric items
  if (sectionType === ECategoryType.MultiNumeric) {
    return itemType === EFeatureFamilyType.Numeric;
  }

  // For category targets that aren't explicitly MultiNumeric, also validate
  if (isTargetCategory && itemType) {
    return itemType === EFeatureFamilyType.Numeric;
  }

  // For non-MultiNumeric sections or no section type, allow any item
  return true;
};

/**
 * Shows category-related error dialog with appropriate message
 * @param dispatch - Redux dispatch function
 * @param t - Translation function
 * @param errorType - Type of error to show
 */
export const showCategoryError = (dispatch: any, t: any, errorType: ECategoryErrorType) => {
  let messageKey: string;

  switch (errorType) {
    case ECategoryErrorType.INVALID_DRAG_DROP:
      messageKey = 'messages.category.inValidCategoryDrag';
      break;
    case ECategoryErrorType.MULTI_NUMERIC_WARNING:
      messageKey = 'messages.category.multiNumericWarning';
      break;
    default:
      messageKey = 'messages.category.genericError';
  }

  dispatch(
    openDialog({
      type: ECommonDialogType.Error,
      data: t(messageKey),
    })
  );
};
